// src/screens/ReviewsModeration.js — moderation queue for user-written content
// (June 2026): inscriptions (passport notes), booking reviews and community
// reviews in one feed, with hide/restore. Hiding maps to each table's own
// mechanism (inscriptions → visibility 'private', booking reviews →
// published=false, community reviews → hidden=true); every action is written
// to the admin audit trail by admin_moderate_content.
import React, { useState, useEffect } from 'react';
import { ConsoleShell, Panel, SectionLabel, Empty, useConsoleTheme, F } from '../components/Console';
import { supabase } from '../supabaseClient';

const KIND_LABEL = { inscription: 'Inscription', booking_review: 'Booking review', community_review: 'Community review' };

function ReviewsModerationBody() {
  const { t } = useConsoleTheme();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState('all'); // all | visible | hidden
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => supabase.rpc('admin_list_user_content', { p_limit: 120 }).then(({ data, error }) => {
    if (error) { setMsg('Load failed: ' + error.message); setItems([]); return; }
    setItems(data || []);
  });
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const moderate = async (it, hide) => {
    setBusyId(it.kind + it.id);
    const { error } = await supabase.rpc('admin_moderate_content', { p_kind: it.kind, p_id: it.id, p_hide: hide });
    setBusyId(null);
    if (error) { setMsg('Moderation failed: ' + error.message); return; }
    setMsg(hide ? 'Hidden from the app (audit-logged).' : 'Restored.');
    setItems(prev => (prev || []).map(x => (x.kind === it.kind && x.id === it.id ? { ...x, hidden: hide } : x)));
    setTimeout(() => setMsg(''), 2600);
  };

  const shown = (items || []).filter(it => filter === 'all' ? true : filter === 'hidden' ? it.hidden : !it.hidden);
  const counts = {
    all: (items || []).length,
    hidden: (items || []).filter(i => i.hidden).length,
  };

  return (
    <div style={{ fontFamily: F }}>
      <div style={{ fontSize: '0.9rem', color: t.mut, margin: '2px 0 16px', lineHeight: 1.5 }}>
        Everything users write that other travellers can see — passport inscriptions, booking reviews and community
        reviews. Hide anything that breaks the content rules; hiding is reversible and audit-logged.
      </div>

      <SectionLabel right={items ? `${counts.all} items · ${counts.hidden} hidden` : undefined}>Moderation queue</SectionLabel>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[['all', 'All'], ['visible', 'Visible'], ['hidden', 'Hidden']].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            style={{ appearance: 'none', cursor: 'pointer', fontFamily: F, fontSize: '0.82rem', fontWeight: 600,
              padding: '5px 14px', borderRadius: 99, border: `1px solid ${filter === k ? t.gold : t.bd}`,
              background: filter === k ? 'rgba(159,126,79,0.12)' : 'transparent', color: filter === k ? t.gold : t.mut }}>
            {label}
          </button>
        ))}
        {msg && <span style={{ alignSelf: 'center', fontSize: '0.8rem', color: t.up, fontWeight: 600 }}>{msg}</span>}
      </div>

      <Panel>
        {!items ? <Empty>Loading user content…</Empty> : shown.length === 0 ? (
          <Empty>{filter === 'hidden' ? 'Nothing is hidden.' : 'No user-written content yet — inscriptions and reviews appear here as travellers write them.'}</Empty>
        ) : shown.map(it => (
          <div key={it.kind + it.id} style={{ display: 'flex', gap: 12, padding: '11px 0', borderBottom: `1px solid ${t.bd}`, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 3 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: t.gold }}>{KIND_LABEL[it.kind] || it.kind}</span>
                {it.rating != null && <span style={{ fontSize: '0.8rem', color: t.warn, fontWeight: 600 }}>{'★'.repeat(Math.max(0, Math.min(5, it.rating)))}</span>}
                <span style={{ fontSize: '0.78rem', color: t.mut }}>{it.who || 'anonymous'}{it.place ? ' · ' + it.place : ''}</span>
                <span style={{ fontSize: '0.72rem', color: t.mut, fontFamily: 'monospace' }}>{(it.at || '').slice(0, 10)}</span>
                {it.hidden && <span style={{ fontSize: '0.66rem', fontWeight: 700, padding: '1px 8px', borderRadius: 9, background: 'rgba(220,53,69,0.12)', color: t.danger }}>HIDDEN</span>}
              </div>
              <div style={{ fontSize: '0.88rem', color: it.hidden ? t.mut : t.tx, lineHeight: 1.55 }}>
                {it.body || <em style={{ color: t.mut }}>(no text)</em>}
              </div>
            </div>
            <button
              onClick={() => moderate(it, !it.hidden)}
              disabled={busyId === it.kind + it.id}
              style={{ flexShrink: 0, cursor: 'pointer', fontFamily: F, fontSize: '0.78rem', fontWeight: 600,
                padding: '6px 14px', borderRadius: 8, border: `1px solid ${it.hidden ? 'rgba(23,168,106,0.4)' : 'rgba(220,53,69,0.35)'}`,
                background: 'transparent', color: it.hidden ? t.up : t.danger }}>
              {busyId === it.kind + it.id ? '…' : it.hidden ? 'Restore' : 'Hide'}
            </button>
          </div>
        ))}
      </Panel>
    </div>
  );
}

export default function ReviewsModeration(props) {
  if (props && props.embed) return <ReviewsModerationBody {...props} />;
  return <ConsoleShell title="Reviews"><ReviewsModerationBody {...props} /></ConsoleShell>;
}
